import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { map } from 'rxjs';
import { FormField, type FieldTree } from '@angular/forms/signals';
import {
  SignalCheckbox,
  SignalDateField,
  SignalDropdown,
  SignalPhoneNumberField,
  SignalTextField,
  SignalTextarea,
  SignalRadioGroup,
  SignalSwitch,
  SignalSlider,
  SignalNumberField,
  SignalDateRangeField,
  SignalRangeSlider,
  OverlayService,
  AppSvgIconComponent,
} from '@vallift/ngl';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { PublicFormsApiService } from '@core/api/public/public-forms-api.service';
import type { PublicFormFillContext } from '../form-preview-card/form-preview-card.component';
import {
  readMultiChoiceDefaults,
  type CurrencyConfig,
  type DividerOpacity,
  DividerStyle,
  DividerThickness,
  FormField as BuiltFormField,
  FormInputAppearance,
  HeadingAlignment,
  HeadingFontFamily,
  HeadingFontStyle,
  HeadingFontWeight,
  HeadingLevel,
  ParagraphAlignment,
  ParagraphFontFamily,
  ParagraphFontStyle,
  ParagraphFontWeight,
  ParagraphLevel,
  type RatingConfig,
  type SpacerSize,
  readMultiChoiceGridConfig,
  SingleChoiceGridConfig,
  readSingleChoiceGridConfig,
  readNumberGridConfig,
} from '../../models/built-form';
import {
  readNumberGridColumnId,
  type NumberGridColumn,
  type NumberGridItem,
} from '@shared/components/number-grid/number-grid';
import {
  CurrencyInput,
  type CurrencyFieldValue,
} from '@shared/components/currency-input/currency-input';
import { Rating } from '@shared/components/rating/rating';
import { CURRENCY_DISPLAY_FALLBACK } from '@shared/components/currency-input/currency.utils';
import {
  resolveCollectionDropdownItems,
  resolveFieldDropdownItemsSync,
  SEARCHABLE_PICKER_PAGE_SIZE,
  type CollectionSelectOption,
  type DropdownOptionItem,
} from '../../utils/resolve-collection-dropdown-items';
import type { AnswerValue, CollectionSelectAnswer } from '../../utils/respondent-answer-form';
import { ImagePicker } from '@shared/components/image-picker/image-picker';
import { GoogleAddressAutoComplete } from '@shared/components/google-address-auto-complete/google-address-auto-complete';
import { MapLocationPickerComponent } from '@shared/components/map-location-picker/map-location-picker';
import { SearchablePickerOverlayComponent } from '../searchable-picker-overlay/searchable-picker-overlay';
import { SingleChoice } from '@shared/components/single-choice/single-choice';
import {
  isMultiChoiceAnswer,
  isMultiChoiceStringAnswer,
  multiChoiceAnswersFromDefaults,
  MultiChoice,
} from '@shared/components/multi-choice/multi-choice';
import {
  MultiChoiceGridConfig,
  MultiChoiceGridOptions,
} from '@shared/components/multi-choice-grid/multi-choice-grid';
import { Choice, SingleChoiceGrid, SingleChoiceGridType } from '@shared/components/single-choice-grid/single-choice-grid';
import { NumberGridComponent } from '@shared/components/number-grid/number-grid';

function isCollectionSelectAnswer(value: AnswerValue | undefined): value is CollectionSelectAnswer {
  return (
    typeof value === 'object' &&
    value != null &&
    !Array.isArray(value) &&
    'id' in value &&
    typeof (value as CollectionSelectAnswer).id === 'string'
  );
}
@Component({
  selector: 'app-form-field-preview-control',
  standalone: true,
  imports: [
    FormField,
    SignalTextField,
    SignalTextarea,
    SignalPhoneNumberField,
    SignalDropdown,
    SignalCheckbox,
    SignalDateField,
    SignalRadioGroup,
    SignalSwitch,
    SignalSlider,
    SignalNumberField,
    ImagePicker,
    SignalDateRangeField,
    SignalRangeSlider,
    GoogleAddressAutoComplete,
    MapLocationPickerComponent,
    AppSvgIconComponent,
    SingleChoice,
    MultiChoice,
    CurrencyInput,
    Rating,
    MultiChoiceGridConfig,
    SingleChoiceGrid,
    NumberGridComponent,
  ],
  templateUrl: './form-field-preview-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldPreviewControlComponent {
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly publicFormsApi = inject(PublicFormsApiService);
  private readonly overlayService = inject(OverlayService);

  readonly field = input.required<BuiltFormField>();
  readonly inputAppearance = input<FormInputAppearance>('fill');
  readonly readonly = input(true);
  readonly answerField = input<FieldTree<AnswerValue> | undefined>(undefined);
  readonly publicContext = input<PublicFormFillContext | null>(null);
  readonly previewMode = input<'desktop' | 'mobile'>('desktop');

  private readonly collectionDropdownItems = signal<CollectionSelectOption[]>([]);
  private readonly multiChoiceGridCollectionItems = signal<CollectionSelectOption[]>([]);

  readonly hasAnswerBinding = computed(() => !this.readonly() && this.answerField() != null);

  readonly isCollectionBackedSelect = computed(
    () =>
      this.field().type === 'select' && Boolean(this.field().dataLibraryCollection?.dataLibraryId),
  );

  readonly isCollectionBackedSingleChoice = computed(
    () =>
      this.field().type === 'single_choice' &&
      Boolean(this.field().dataLibraryCollection?.dataLibraryId),
  );

  readonly isCollectionBackedMultiChoice = computed(
    () =>
      this.field().type === 'multi_choice' &&
      Boolean(this.field().dataLibraryCollection?.dataLibraryId),
  );

  readonly isCollectionBackedMultiChoiceGrid = computed(() => {
    const field = this.field();
    const config = readMultiChoiceGridConfig(field);

    return Boolean(config.itemsDataLibraryCollection?.dataLibraryId);
  });

  readonly isCollectionBackedNumberGrid = computed(() => {
    if (this.field().type !== 'number_grid') {
      return false;
    }
    const collection = readNumberGridConfig(this.field()).itemsDataLibraryCollection;
    return Boolean(collection?.dataLibraryId && collection?.displayFieldKey);
  });

  private readonly numberGridCollectionItems = signal<CollectionSelectOption[]>([]);

  readonly isSearchablePicker = computed(() => this.field().type === 'searchable_picker');

  readonly searchablePickerAnswer = computed((): CollectionSelectAnswer | null => {
    const control = this.answerField();
    if (!control) {
      return null;
    }
    const value = control().value();
    return isCollectionSelectAnswer(value) ? value : null;
  });

  readonly searchablePickerPrimary = computed(
    () => this.searchablePickerAnswer()?.display?.trim() || '',
  );

  readonly searchablePickerSecondary = computed(
    () => this.searchablePickerAnswer()?.secondaryDisplay?.trim() || '',
  );

  readonly manualDropdownItems = computed((): DropdownOptionItem[] => {
    if (this.isCollectionBackedSelect()) {
      return [];
    }
    return resolveFieldDropdownItemsSync(this.field());
  });

  readonly collectionSelectItems = computed(() => this.collectionDropdownItems());

  readonly collectionSelectValue = computed(() => {
    const control = this.answerField();
    if (!control) {
      return null;
    }
    const value = control().value();
    return isCollectionSelectAnswer(value) ? value.id : null;
  });

  readonly isRequired = computed(
    () => this.field().required === true || this.field().validation?.required === true,
  );

  readonly isFieldDisabled = computed(() => this.field().disabled === true);

  readonly isRatingField = computed(() => this.field().type === 'rating');

  readonly placeholder = computed(() => this.field().placeholder ?? '');

  readonly headingLevel = computed((): HeadingLevel => this.field().headingLevel ?? 2);

  readonly spacerSize = computed((): SpacerSize => this.field().spacerSize ?? 'md');

  readonly headingAlignment = computed(
    (): HeadingAlignment => this.field().headingAlignment ?? 'left',
  );

  readonly headingColor = computed(() => this.field().headingColor ?? '#0f172a');

  readonly headingFontStyle = computed(
    (): HeadingFontStyle => this.field().headingFontStyle ?? 'normal',
  );

  readonly headingFontFamily = computed(
    (): HeadingFontFamily => this.field().headingFontFamily ?? 'Arial',
  );

  readonly headingFontWeight = computed(
    (): HeadingFontWeight => this.field().headingFontWeight ?? 400,
  );

  readonly paragraphLevel = computed((): ParagraphLevel => this.field().paragraphLevel ?? 2);

  readonly paragraphAlignment = computed(
    (): ParagraphAlignment => this.field().paragraphAlignment ?? 'left',
  );

  readonly paragraphFontStyle = computed(
    (): ParagraphFontStyle => this.field().paragraphFontStyle ?? 'normal',
  );

  readonly paragraphFontFamily = computed(
    (): ParagraphFontFamily => this.field().paragraphFontFamily ?? 'Arial',
  );

  readonly paragraphColor = computed(() => this.field().paragraphColor ?? '#475569');

  readonly paragraphFontWeight = computed(
    (): ParagraphFontWeight => this.field().paragraphFontWeight ?? 400,
  );

  readonly dividerStyle = computed((): DividerStyle => this.field().dividerStyle ?? 'solid');

  readonly dividerThickness = computed((): DividerThickness => this.field().dividerThickness ?? 1);

  readonly dividerColor = computed(() => this.field().dividerColor ?? '#e2e8f0');

  readonly dividerOpacity = computed((): DividerOpacity => this.field().dividerOpacity ?? 100);

  readonly textareaRows = computed(() => this.field().textareaRows ?? 3);

  readonly textareaResize = computed(() => this.field().textareaResize ?? 'none');

  readonly textareaCharacterLimit = computed(() => this.field().textareaCharacterLimit);

  readonly singleChoiceDefaultValue = computed(() => {
    const bound = this.field().default;
    if (bound?.source === 'static' && typeof bound.value === 'string') {
      return bound.value;
    }
    const value = this.field().config?.defaultValue;
    return typeof value === 'string' ? value : '';
  });

  /** Value for non-formField / disabled display (id string or option text). */
  readonly singleChoiceDisplayValue = computed(() => {
    const control = this.answerField();
    if (control) {
      const value = control().value();
      if (isCollectionSelectAnswer(value)) {
        return value.id;
      }
      if (typeof value === 'string') {
        return value;
      }
    }
    return this.singleChoiceDefaultValue();
  });

  readonly singleChoiceOptions = computed(() => {
    const field = this.field();

    if (field.type !== 'single_choice') {
      return [];
    }

    if (this.isCollectionBackedSingleChoice()) {
      return this.collectionDropdownItems().map((item) => ({
        value: item.id,
        label: item.display,
        data: item,
      }));
    }

    const legacyConfigOptions =
      field.config && 'options' in field.config && Array.isArray(field.config.options)
        ? field.config.options.filter((option): option is string => typeof option === 'string')
        : null;
    const options = field.options?.length
      ? field.options
      : (legacyConfigOptions ?? ['Option 1', 'Option 2', 'Option 3']);

    return options.map((option) => ({
      value: option,
      label: option,
    }));
  });

  readonly multiChoiceDefaultValue = computed(() => readMultiChoiceDefaults(this.field()));

  readonly multiChoiceDisplayValue = computed(() => {
    const control = this.answerField();
    if (control) {
      const value = control().value();
      if (this.isCollectionBackedMultiChoice()) {
        if (isMultiChoiceAnswer(value)) {
          return value;
        }
      } else if (isMultiChoiceStringAnswer(value)) {
        return value;
      } else if (isMultiChoiceAnswer(value)) {
        // Legacy static answers stored as objects → display ids/labels.
        return value.map((item) => item.display || item.id);
      }
    }
    if (this.isCollectionBackedMultiChoice()) {
      return multiChoiceAnswersFromDefaults(
        this.multiChoiceDefaultValue(),
        this.multiChoiceOptions(),
      );
    }
    return this.multiChoiceDefaultValue();
  });

  readonly multiChoiceOptions = computed(() => {
    const field = this.field();

    if (field.type !== 'multi_choice') {
      return [];
    }

    if (this.isCollectionBackedMultiChoice()) {
      return this.collectionDropdownItems().map((item) => ({
        value: item.id,
        label: item.display,
        data: item,
      }));
    }

    const legacyConfigOptions =
      field.config && 'options' in field.config && Array.isArray(field.config.options)
        ? field.config.options.filter((option): option is string => typeof option === 'string')
        : null;
    const options = field.options?.length
      ? field.options
      : (legacyConfigOptions ?? ['Option 1', 'Option 2', 'Option 3']);

    return options.map((option) => ({
      value: option,
      label: option,
    }));
  });

  readonly multiChoiceGridAllowAdditionalRows = computed(
    () => this.multiChoiceGridConfig().allowAdditionalRows ?? false,
  );
  readonly textareaShowCharacterCount = computed(
    () => this.field().textareaShowCharacterCounter ?? false,
  );

  readonly textDefaultValue = computed(() => {
    const bound = this.field().default;
    if (bound?.source === 'variable' || bound?.source === 'system') {
      if (typeof bound.value === 'string') {
        return bound.value;
      }
      if (typeof bound.value === 'number' || typeof bound.value === 'boolean') {
        return String(bound.value);
      }
      return '';
    }
    if (bound?.source === 'static' && typeof bound.value === 'string') {
      return bound.value;
    }
    const value = this.field().config?.defaultValue;
    return typeof value === 'string' ? value : '';
  });

  readonly textareaDefaultValue = computed(() => {
    const value = this.field().config?.defaultValue;
    return typeof value === 'string' ? value : '';
  });

  readonly selectDefaultValue = computed(() => {
    const value = this.field().config?.defaultValue;
    return typeof value === 'string' ? value : '';
  });

  readonly checkboxDefaultValue = computed(() => {
    const value = this.field().config?.defaultValue;
    return typeof value === 'boolean' ? value : false;
  });

  readonly numberDefaultValue = computed(() => {
    const bound = this.field().default;
    if (bound?.source === 'variable' || bound?.source === 'system') {
      if (typeof bound.value === 'number') {
        return bound.value;
      }
      if (typeof bound.value === 'string' && bound.value.trim() !== '') {
        const parsed = Number(bound.value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    }
    if (bound?.source === 'static' && typeof bound.value === 'number') {
      return bound.value;
    }
    const value = this.field().config?.defaultValue;
    return typeof value === 'number' ? value : null;
  });

  readonly numberPlaceholder = computed(() => this.placeholder() || '');

  private readonly currencyConfig = computed((): CurrencyConfig => {
    const config = this.field().config;
    if (!config) {
      return {};
    }
    return {
      defaultCurrency:
        'defaultCurrency' in config && typeof config.defaultCurrency === 'string'
          ? config.defaultCurrency
          : 'defaultCurrency' in config && config.defaultCurrency === null
            ? null
            : undefined,
      defaultValue:
        'defaultValue' in config && typeof config.defaultValue === 'number'
          ? config.defaultValue
          : undefined,
      allowChangeCurrency:
        'allowChangeCurrency' in config && typeof config.allowChangeCurrency === 'boolean'
          ? config.allowChangeCurrency
          : undefined,
    };
  });

  /** Locked default from config, or null when respondents may change currency. */
  readonly currencyConfiguredDefault = computed((): string | null => {
    const code = this.currencyConfig().defaultCurrency?.trim();
    return code ? code : null;
  });

  /** Currency shown in the control (configured default, else USD). */
  readonly currencyDefaultCurrency = computed(
    () => this.currencyConfiguredDefault() ?? CURRENCY_DISPLAY_FALLBACK,
  );

  readonly currencyAllowChange = computed(() => {
    const config = this.currencyConfig();
    if (typeof config.allowChangeCurrency === 'boolean') {
      return config.allowChangeCurrency;
    }
    return this.currencyConfiguredDefault() == null;
  });

  readonly currencyMin = computed(() => this.field().validation?.min ?? null);

  readonly currencyMax = computed(() => this.field().validation?.max ?? null);

  readonly currencyDefaultValue = computed((): CurrencyFieldValue => {
    const currencyType = this.currencyDefaultCurrency();
    const bound = this.field().default;
    let amount: number | null = null;
    if (bound?.source === 'static' && typeof bound.value === 'number') {
      amount = bound.value;
    } else {
      const configAmount = this.currencyConfig().defaultValue;
      amount = typeof configAmount === 'number' ? configAmount : null;
    }
    return { currencyType, amount };
  });

  readonly switchDefaultValue = computed(() => {
    const value = this.field().config?.defaultValue;
    return typeof value === 'boolean' ? value : false;
  });

  readonly sliderMin = computed(() => this.field().sliderMin ?? 0);

  readonly sliderMax = computed(() => this.field().sliderMax ?? 100);

  readonly sliderStep = computed(() => this.field().sliderStep ?? 1);

  readonly sliderDefaultValue = computed(() => {
    const value = this.field().config?.defaultValue;
    return typeof value === 'number' ? value : 50;
  });

  readonly ratingConfig = computed((): RatingConfig => {
    const config = this.field().config;
    if (!config) {
      return {};
    }
    return {
      max: 'max' in config && (config.max === 5 || config.max === 10) ? config.max : undefined,
    };
  });

  readonly numberGridConfig = computed(() => {
    if (this.field().type !== 'number_grid') {
      return undefined;
    }
    return readNumberGridConfig(this.field());
  });

  readonly numberGridColumns = computed((): NumberGridColumn[] => {
    const raw = this.numberGridConfig()?.columns ?? [];
    return raw
      .map((column) => {
        const id = readNumberGridColumnId(column as { id?: string; key?: string });
        const label =
          typeof column.label === 'string' ? column.label : id;
        return id ? { id, label } : null;
      })
      .filter((column): column is NumberGridColumn => column != null);
  });

  readonly numberGridItems = computed((): NumberGridItem[] => {
    if (this.isCollectionBackedNumberGrid()) {
      return this.numberGridCollectionItems().map((item) => ({
        id: item.id,
        uniqueKey: item.uniqueKey,
        display: item.display,
      }));
    }

    return (this.numberGridConfig()?.items ?? []).map((item) => {
      if (typeof item === 'string') {
        return { display: item };
      }
      return {
        ...(item.id ? { id: item.id } : {}),
        ...(item.uniqueKey ? { uniqueKey: item.uniqueKey } : {}),
        display: item.display ?? '',
      };
    });
  });

  readonly ratingMax = computed(() => this.ratingConfig().max ?? 5);

  readonly dateRangeValue = computed(() => {
    const startDate = this.field().startDate;
    const endDate = this.field().endDate;

    if (!startDate || !endDate) {
      return null;
    }

    return {
      startDate,
      endDate,
    };
  });

  readonly rangeSliderDefaultValue = computed(() => {
    const value = this.field().config?.defaultValue;

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const range = value as { start: number; end: number };

      return {
        start: range.start,
        end: range.end,
      };
    }

    return {
      start: this.sliderMin(),
      end: this.sliderMax(),
    };
  });

  readonly signalDateFormat = computed(() => {
    switch (this.field().dateFormat) {
      case 'DD/MM/YYYY':
        return 'ddmmyyyy';

      case 'MM/DD/YYYY':
        return 'mmddyyyy';

      default:
        return 'ddmmyyyy';
    }
  });

  readonly radioOptions = computed(() =>
    (this.field().options ?? []).map((option) => ({
      value: option,
      label: option,
    })),
  );


  protected readonly model = model;
  readonly answerFieldError = computed(() => {
    const control = this.answerField();
    if (!control || !this.hasAnswerBinding()) {
      return '';
    }
    const state = control();
    if (!state.touched() || !state.invalid()) {
      return '';
    }
    const errors = state.errors();
    const first = errors[0];
    return first && 'message' in first ? String(first.message ?? '') : '';
  });

  constructor() {
    effect((onCleanup) => {
      const isSingleChoiceGrid = this.isCollectionBackedSingleChoiceGrid();
      const isMultiChoiceGrid = this.isCollectionBackedMultiChoiceGrid();
      const isNumberGrid = this.isCollectionBackedNumberGrid();
      const loadCollectionItems =
        this.isCollectionBackedSelect() ||
        this.isCollectionBackedSingleChoice() ||
        this.isCollectionBackedMultiChoice() ||
        isMultiChoiceGrid ||
        isSingleChoiceGrid ||
        isNumberGrid;

      if (!loadCollectionItems) {
        this.collectionDropdownItems.set([]);
        this.singleChoiceGridCollectionItems.set([]);
        this.multiChoiceGridCollectionItems.set([]);
        this.numberGridCollectionItems.set([]);
        return;
      }

      const field = this.field();
      const collection = isSingleChoiceGrid
        ? this.singleChoiceGridConfig().itemsDataLibraryCollection
        : isMultiChoiceGrid
          ? readMultiChoiceGridConfig(field).itemsDataLibraryCollection
          : isNumberGrid
            ? readNumberGridConfig(field).itemsDataLibraryCollection
            : field.dataLibraryCollection;

      const collectionId = collection?.dataLibraryId?.trim();
      const displayFieldKey = collection?.displayFieldKey?.trim();

      if (!collectionId) {
        this.collectionDropdownItems.set([]);
        this.singleChoiceGridCollectionItems.set([]);
        this.multiChoiceGridCollectionItems.set([]);
        this.numberGridCollectionItems.set([]);
        return;
      }

      const publicCtx = this.publicContext();
      // Grid row collections live on config.itemsDataLibraryCollection; public
      // listFieldOptions historically only read field.dataLibraryCollection.
      const usePublicFieldOptions =
        Boolean(publicCtx) &&
        !isSingleChoiceGrid &&
        !isMultiChoiceGrid &&
        !isNumberGrid;

      const options$ =
        usePublicFieldOptions && publicCtx
          ? this.publicFormsApi
              .listFieldOptions(
                publicCtx.workspaceSlug,
                publicCtx.formSlug,
                this.field().id,
                { limit: 100 },
                publicCtx.auth,
              )
              .pipe(
                map((response) =>
                  (response.data?.options ?? []).map(
                    (option): CollectionSelectOption => ({
                      id: option.id,
                      uniqueKey: option.uniqueKey?.trim() || option.id,
                      display: option.label,
                      ...(option.secondaryLabel ? { secondaryDisplay: option.secondaryLabel } : {}),
                    }),
                  ),
                ),
              )
          : resolveCollectionDropdownItems(this.workspaceApi, collectionId, displayFieldKey);

      const sub = options$.subscribe({
        next: (items) => {
          if (isSingleChoiceGrid) {
            this.singleChoiceGridCollectionItems.set(items);
            return;
          }

          if (isMultiChoiceGrid) {
            this.multiChoiceGridCollectionItems.set(items);
            return;
          }

          if (isNumberGrid) {
            this.numberGridCollectionItems.set(items);
            return;
          }

          this.collectionDropdownItems.set(items);

          const control = this.answerField();
          if (!control) {
            return;
          }

          const current = control().value();

          if (isMultiChoiceAnswer(current)) {
            let changed = false;

            const next = current.map((item) => {
              const match = items.find((option) => option.id === item.id);

              if (
                !match ||
                (item.display === match.display && item.uniqueKey === match.uniqueKey)
              ) {
                return item;
              }

              changed = true;

              return {
                id: match.id,
                uniqueKey: match.uniqueKey,
                display: match.display,
                ...(match.secondaryDisplay != null
                  ? { secondaryDisplay: match.secondaryDisplay }
                  : {}),
              };
            });

            if (changed) {
              control().value.set(next);
            }

            return;
          }

          if (!isCollectionSelectAnswer(current) || !current.id) {
            return;
          }

          const match = items.find((item) => item.id === current.id);

          if (!match) {
            return;
          }

          if (current.display !== match.display || current.uniqueKey !== match.uniqueKey) {
            control().value.set({
              id: match.id,
              uniqueKey: match.uniqueKey,
              display: match.display,
            });
          }
        },
        error: () => {
          if (isSingleChoiceGrid) {
            this.singleChoiceGridCollectionItems.set([]);
          } else if (isMultiChoiceGrid) {
            this.multiChoiceGridCollectionItems.set([]);
          } else {
            this.collectionDropdownItems.set([]);
          }
        },
      });

      onCleanup(() => sub.unsubscribe());
    });
  }

  openSearchablePicker(): void {
    if (this.readonly() && !this.hasAnswerBinding()) {
    }
    const collection = this.field().dataLibraryCollection;
    const collectionId = collection?.dataLibraryId?.trim();
    const primaryKey = collection?.displayFieldKey?.trim();
    const secondaryKey = collection?.secondaryDisplayFieldKey?.trim();
    if (!collectionId || !primaryKey || !secondaryKey) {
      return;
    }

    const current = this.searchablePickerAnswer();
    const publicCtx = this.publicContext();
    const dialogRef = this.overlayService.openModal(SearchablePickerOverlayComponent, {
      fitContent: true,
      panelClass: ['w-[26rem]', 'max-w-[calc(100%-2rem)]'],
      data: {
        title: this.field().label,
        collectionId,
        primaryKey,
        secondaryKey,
        selectedId: current?.id ?? null,
        pageSize: collection?.pageSize ?? SEARCHABLE_PICKER_PAGE_SIZE,
        ...(publicCtx
          ? {
              publicField: {
                workspaceSlug: publicCtx.workspaceSlug,
                formSlug: publicCtx.formSlug,
                fieldId: this.field().id,
                auth: publicCtx.auth,
              },
            }
          : {}),
      },
    });

    dialogRef.closed.subscribe((result: CollectionSelectAnswer | null | undefined) => {
      if (result === undefined) {
        return;
      }
      const control = this.answerField();
      if (!control) {
        return;
      }
      control().value.set(result);
    });
  }

  onCollectionSelectChange(value: unknown): void {
    const control = this.answerField();
    if (!control) {
      return;
    }
    if (value == null || value === '') {
      control().value.set(null);
      return;
    }
    const id = typeof value === 'string' ? value : String(value);
    const option = this.collectionDropdownItems().find((item) => item.id === id);
    if (!option) {
      control().value.set(null);
      return;
    }
    control().value.set({
      id: option.id,
      uniqueKey: option.uniqueKey,
      display: option.display,
    });
  }

  isCheckboxOptionSelected(option: string): boolean {
    const control = this.answerField();
    if (!control) {
      return false;
    }
    const value = control().value();
    return (
      Array.isArray(value) && value.some((item) => typeof item === 'string' && item === option)
    );
  }

  onCheckboxOptionChange(option: string, checked: boolean): void {
    const control = this.answerField();
    if (!control) {
      return;
    }
    const current = control().value();
    const selected = Array.isArray(current)
      ? current.filter((item): item is string => typeof item === 'string')
      : [];
    const next = checked
      ? selected.includes(option)
        ? selected
        : [...selected, option]
      : selected.filter((item) => item !== option);
    control().value.set(next);
  }

  onImageFilesChange(files: File[]): void {
    const control = this.answerField();
    if (!control) {
      return;
    }
    control().value.set(files);
  }

  readonly multiChoiceGridConfig = computed(() => readMultiChoiceGridConfig(this.field()));

  readonly multiChoiceGridResponseType = computed(() => {
    const config = this.field().config;

    if (
      config &&
      'responseType' in config &&
      (config.responseType === 'checkbox' ||
        config.responseType === 'dropdown' ||
        config.responseType === 'button')
    ) {
      return config.responseType;
    }
    return 'checkbox';
  });

  readonly multiChoiceGridItems = computed(() => {
    if (this.isCollectionBackedMultiChoiceGrid()) {
      return this.multiChoiceGridCollectionItems().map((item) => ({
        id: item.id,
        uniqueKey: item.uniqueKey,
        display: item.display,
      }));
    }

    return this.multiChoiceGridConfig().items;
  });

  readonly multiChoiceGridOptions = computed(() => {
    const config = this.multiChoiceGridConfig();

    return config.options.map((option) => ({
      value: option.id,
      label: option.label,
    }));
  });

  readonly multiChoiceGridAddRowButtonLabel = computed(
    () => this.multiChoiceGridConfig().addRowButtonLabel ?? '+ Add another item',
  );

  readonly singleChoiceGridConfig = computed(
    (): SingleChoiceGridConfig => readSingleChoiceGridConfig(this.field()),
  );

  readonly isCollectionBackedSingleChoiceGrid = computed(() => {
    if (this.field().type !== 'single_choice_grid') {
      return false;
    }
    const collection = this.singleChoiceGridConfig().itemsDataLibraryCollection;
    return Boolean(collection?.dataLibraryId && collection?.displayFieldKey);
  });

  private readonly singleChoiceGridCollectionItems = signal<CollectionSelectOption[]>([]);

  readonly singleChoiceGridType = computed((): SingleChoiceGridType => {
    const type = this.singleChoiceGridConfig().responseType;

    return type === 'button' || type === 'radio' || type === 'dropdown' ? type : 'button';
  });

  readonly singleChoiceGridItems = computed(() => {
    const config = this.singleChoiceGridConfig();
    if (this.isCollectionBackedSingleChoiceGrid()) {
      return this.singleChoiceGridCollectionItems();
    }

    return config.items ?? [];
  });

  readonly singleChoiceGridOptions = computed((): Choice[] => {
    const config = this.singleChoiceGridConfig();

    const options = config.choice?.length ? config.choice : (config.options ?? []);

    return options.map((option) => ({
      value: option.id,
      label: option.label,
    }));
  });

  readonly singleChoiceGridAllowAdditionalRows = computed(
    () => this.singleChoiceGridConfig().allowAdditionalRows,
  );

  readonly singleChoiceGridAddRowButtonLabel = computed(
    () => this.singleChoiceGridConfig().addRowButtonLabel?.trim() || '+ Add another item',
  );

  readonly multiChoiceGridDisplayValue = computed<MultiChoiceGridOptions[]>(() => {
    const seedFromItems = (): MultiChoiceGridOptions[] =>
      this.multiChoiceGridItems().map((item) => {
        if (typeof item === 'string') {
          return { display: item, selectedChoiceIds: [] };
        }
        return {
          id: item.id,
          uniqueKey: item.uniqueKey,
          display: item.display,
          selectedChoiceIds: [],
        };
      });

    const control = this.answerField();
    if (!control) {
      return seedFromItems();
    }

    const value = control().value();
    if (!Array.isArray(value) || value.length === 0) {
      return seedFromItems();
    }

    const rows = value.filter(
      (item): item is MultiChoiceGridOptions =>
        typeof item === 'object' &&
        item !== null &&
        'display' in item &&
        typeof (item as MultiChoiceGridOptions).display === 'string',
    );

    if (rows.length === 0) {
      return seedFromItems();
    }

    return rows.map((row) => ({
      ...row,
      selectedChoiceIds: Array.isArray(row.selectedChoiceIds) ? row.selectedChoiceIds : [],
    }));
  });
}
